import { ErrorCode, type JsonRpcMessage, type JsonValue } from '../protocol/jsonRpc';
import { parseMethodName } from '../protocol/methodName';
import { defaultsInterface, directoryInterface, directoryWatchNever, schemasInterface, type RootPrincipalSet } from '../hub/common/reflection.interfaces';
import type {
    InterfaceClient,
    InterfaceDefinition,
    InterfaceHandlers,
    StreamApi,
} from './interfaceDefinition';
import { type MemberType, NotificationType, RequestType } from '../schema/memberTypes';
import { safeParse } from 'zod/v4/core';
import type { IMessageTransport } from '../transport/messageTransport';
import {
    Channel,
    type MessageWithCtx,
    type IncomingCall,
    type IncomingStream,
    type IRequestSender, type Result,
    RpcError,
    type StreamSendOpts
} from './channel';
import { JsonRpcChannel } from './jsonRpcChannel';

/**
 * High-level hubrpc connection. Layers method-name routing and zod-driven
 * validation on top of a plain JSON-RPC channel.
 *
 * Typed proxies built by this class merely forward each call's
 * `interfaceHash` hint to the channel so the signer stamps it into the
 * `$hubrpc` envelope.
 *
 * `TInCtx` is the per-call out-of-band context the connection's transport
 * carries. Default `undefined` covers cross-process and ordinary in-process
 * transports. The hub's self/overlay connections instantiate with a
 * concrete `TInCtx` (e.g. `Participant`) so handlers can see who originated
 * the call.
 *
 * `TOutCtx` is the per-call override / extension bag the outbound sender
 * understands (see {@link SendOpts.ctx}). It only ever flows into the
 * sender as input, so it is a contravariant type parameter. Default `any`
 * keeps the bare `HubRpcConnection` a valid supertype for holders that do
 * not care about the outbound ctx; pass a concrete shape (e.g.
 * `SigningCallCtx`) to get precise {@link get} typing. The connection
 * itself is agnostic to its contents — it merely forwards the `ctx`
 * defaults supplied to {@link get}.
 */
export class HubRpcConnection<TInCtx = any, TOutCtx = any> {
    /**
     * Convenience: build a {@link JsonRpcChannel} `Channel` from the
     * given transport and wrap it in an `HubRpcConnection`. Use this
     * when you have a transport at hand and don't need a decorator
     * stack (e.g. signing).
     */
    public static fromTransport<TInCtx = undefined>(
        transport: IMessageTransport<MessageWithCtx<TInCtx>, JsonRpcMessage>,
    ): HubRpcConnection<TInCtx> {
        return new HubRpcConnection<TInCtx>(JsonRpcChannel.create<TInCtx>(transport));
    }

    /** Underlying JSON-RPC sender — useful for callers that need raw access (e.g. to call hub-served methods that bypass the interface registry). */
    public readonly channel: IRequestSender<TOutCtx>;

    /** key = `${serviceId ?? ""}::${interfaceId}` */
    private readonly _registry = new Map<string, RegisteredInterface>();

    /** Descriptions for services that have been registered with a `serviceDescription`. */
    private readonly _serviceDescriptions = new Map<string, string>();

    /** Root-node-id requirement sets recorded per serviceId. */
    private readonly _serviceRootPrincipalSets = new Map<string, readonly RootPrincipalSet[]>();

    private _preset: Preset | undefined;

    /**
     * Construct from a {@link Channel} (binds the inbound handler and uses
     * `channel.sender` for outbound calls) or from a bare
     * {@link IRequestSender} (send-only — no inbound handler is registered,
     * useful for bootstrap flows like `createManagedIdentity`).
     */
    constructor(channel: Channel<TInCtx, TOutCtx> | IRequestSender<TOutCtx>) {
        if ('sender' in channel) {
            this.channel = channel.sender;
            channel.setRequestHandler({
                handleRequest: (c) => this._handleRequest(c),
                handleNotification: (c) => this._handleNotification(c),
            });
        } else {
            this.channel = channel;
        }
    }

    /** Get a typed client for `iface`, routed to the implicit (root) service. */
    public get<TDef extends InterfaceDefinition<any>>(
        iface: TDef,
        opts: GetOptions<TOutCtx> = {},
    ): InterfaceClient<TDef> {
        return this._buildClient(iface, opts) as InterfaceClient<TDef>;
    }

    /** Get a service-scoped handle; all interfaces obtained from it route via `serviceId` (form 3). */
    public service(serviceId: string): ServiceHandle<TInCtx, TOutCtx> {
        return new ServiceHandle<TInCtx, TOutCtx>(this, serviceId);
    }

    /** Register handlers for an interface on this connection's server side. */
    public register<TDef extends InterfaceDefinition<any>>(
        iface: TDef,
        handlers: InterfaceHandlers<TDef, TInCtx>,
        opts: RegisterOptions = {},
    ): InterfaceRegistration {
        const serviceId = opts.serviceId;
        const key = `${serviceId ?? ''}::${iface.info.id}`;
        if (this._registry.has(key)) {
            throw new Error(
                `Interface "${iface.info.id}" already registered${serviceId ? ` under service "${serviceId}"` : ''}.`,
            );
        }

        if (opts.serviceDescription !== undefined) {
            if (serviceId === undefined) {
                throw new Error('register: `serviceDescription` requires `serviceId`.');
            }
            const existing = this._serviceDescriptions.get(serviceId);
            if (existing !== undefined && existing !== opts.serviceDescription) {
                throw new Error(
                    `register: conflicting descriptions for service "${serviceId}".`,
                );
            }
            this._serviceDescriptions.set(serviceId, opts.serviceDescription);
        }

        if (opts.rootPrincipalSets !== undefined) {
            if (serviceId === undefined) {
                throw new Error('register: `rootPrincipalSets` requires `serviceId`.');
            }
            const existing = this._serviceRootPrincipalSets.get(serviceId);
            if (existing !== undefined && !rootPrincipalSetsEqual(existing, opts.rootPrincipalSets)) {
                throw new Error(
                    `register: conflicting rootPrincipalSets for service "${serviceId}".`,
                );
            }
            this._serviceRootPrincipalSets.set(serviceId, opts.rootPrincipalSets);
        }

        const entry: RegisteredInterface = {
            iface,
            handlers: handlers as Record<string, (p: any, c: any) => any>,
            serviceId,
        };
        this._registry.set(key, entry);

        let disposed = false;
        return {
            dispose: () => {
                if (disposed) return;
                disposed = true;
                if (this._registry.get(key) !== entry) return;

                this._registry.delete(key);
                if (
                    serviceId === undefined
                    && this._preset?.interfaceId === iface.info.id
                ) {
                    this._preset = undefined;
                }
                if (
                    serviceId !== undefined
                    && !Array.from(this._registry.values()).some((registered) =>
                        registered.serviceId === serviceId)
                ) {
                    this._serviceDescriptions.delete(serviceId);
                    this._serviceRootPrincipalSets.delete(serviceId);
                }
            },
        };
    }

    /**
     * Declare the preset interface for form-1 (bare-method) dispatch. The
     * interface must already be registered under the root (no serviceId).
     * Surfaced via `hubrpc.defaults::get`.
     */
    public setPreset(iface: InterfaceDefinition<any>): void {
        const key = `::${iface.info.id}`;
        if (!this._registry.has(key)) {
            throw new Error(`setPreset: interface "${iface.info.id}" is not registered under the root service.`);
        }
        this._preset = {
            serviceId: undefined,
            interfaceId: iface.info.id,
            hash: iface.schemaHash,
        };
    }

    /** Snapshot of every interface currently registered on this connection. */
    public listRegisteredInterfaces(): readonly {
        readonly serviceId: string;
        readonly interfaceId: string;
        readonly interfaceHash: string;
        readonly serviceDescription?: string;
        readonly rootPrincipalSets?: readonly RootPrincipalSet[];
    }[] {
        return Array.from(this._registry.values()).map((r) => {
            const sid = r.serviceId ?? '';
            const item: {
                serviceId: string;
                interfaceId: string;
                interfaceHash: string;
                serviceDescription?: string;
                rootPrincipalSets?: readonly RootPrincipalSet[];
            } = {
                serviceId: sid,
                interfaceId: r.iface.info.id,
                interfaceHash: r.iface.schemaHash,
            };
            const desc = sid === '' ? undefined : this._serviceDescriptions.get(sid);
            if (desc !== undefined) item.serviceDescription = desc;
            const sets = sid === '' ? undefined : this._serviceRootPrincipalSets.get(sid);
            if (sets !== undefined) item.rootPrincipalSets = sets;
            return item;
        });
    }

    /**
     * Look up a registered interface definition by id (and optional content
     * hash). Returns `undefined` if no registered interface matches.
     */
    public findRegisteredInterface(
        interfaceId: string,
        hash?: string,
    ): InterfaceDefinition<any> | undefined {
        for (const r of this._registry.values()) {
            if (r.iface.info.id !== interfaceId) continue;
            if (hash !== undefined && r.iface.schemaHash !== hash) continue;
            return r.iface;
        }
        return undefined;
    }

    /**
     * Register the three hubrpc reflection interfaces (`defaults`,
     * `directory`, `schemas`), backed by this connection's live registry.
     *
     * By default they live under the root service (form-2 reachable as
     * `hubrpc.directory::list`). Pass `serviceId` to additionally mount
     * them under a specific service — useful for participants that live
     * behind a hub, so callers can reach reflection via form-3
     * `<serviceId>::hubrpc.directory::list`.
     *
     * Idempotent: re-registering the same `(serviceId, interfaceId)` pair
     * is a no-op.
     */
    public enableReflection(opts: { serviceId?: string; } = {}): InterfaceRegistration {
        const key = `${opts.serviceId ?? ''}::${defaultsInterface.info.id}`;
        if (this._registry.has(key)) return { dispose() { } };
        const prefix = `${opts.serviceId ?? ''}::`;
        for (const iface of [directoryInterface, schemasInterface]) {
            if (this._registry.has(`${prefix}${iface.info.id}`)) {
                throw new Error(
                    `enableReflection: partial reflection registration for service "${opts.serviceId ?? ''}".`,
                );
            }
        }

        const regOpts: RegisterOptions = opts.serviceId !== undefined ?
            { serviceId: opts.serviceId } :
            {};
        const registrations: InterfaceRegistration[] = [];

        registrations.push(this.register(defaultsInterface, {
            get: () => {
                if (!this._preset) return {};
                return {
                    serviceId: this._preset.serviceId,
                    interfaceId: this._preset.interfaceId,
                    interfaceHash: this._preset.hash,
                };
            },
        }, regOpts));

        registrations.push(this.register(directoryInterface, {
            list: ({ interfaceId, serviceId, cursor, limit }) => {
                const all = this.listRegisteredInterfaces()
                    .filter((r) => interfaceId === undefined || r.interfaceId === interfaceId)
                    .filter((r) => serviceId === undefined || r.serviceId === serviceId);
                const start = cursor === undefined ? 0 : Number.parseInt(cursor, 10) || 0;
                const end = limit === undefined ? all.length : Math.min(all.length, start + limit);
                const page = all.slice(start, end).map((r) => ({
                    serviceId: r.serviceId,
                    interfaceId: r.interfaceId,
                    interfaceHash: r.interfaceHash,
                    serviceDescription: r.serviceDescription,
                    rootPrincipalSets: r.rootPrincipalSets?.map((set) => set.map((p) => ({ ...p }))),
                }));
                return end < all.length ?
                    { items: page, nextCursor: String(end) } :
                    { items: page };
            },
            watch: directoryWatchNever,
        }, regOpts));

        registrations.push(this.register(schemasInterface, {
            get: ({ interfaceId, hash }) => {
                const iface = this.findRegisteredInterface(interfaceId, hash);
                if (!iface) {
                    throw new RpcError(
                        'Interface not found',
                        ErrorCode.methodNotFound,
                        { reason: 'unknown-interface', interfaceId, hash },
                    );
                }
                return { schema: iface.toSchema() as unknown };
            },
        }, regOpts));

        // A service-scoped reflection set is *also* surfaced at the connection
        // root, so a hub that reads this connection's root directory
        // (`H·root → P`) discovers its `<serviceId>::hubrpc.directory` referral
        // without reaching the (capability-gated) service directory. Keyed
        // separately and guarded above, so the recursive root call is a no-op
        // once root reflection exists.
        if (opts.serviceId !== undefined) {
            try {
                this.enableReflection();
            } catch (error) {
                for (const registration of registrations.reverse()) {
                    registration.dispose();
                }
                throw error;
            }
        }

        let disposed = false;
        return {
            dispose: () => {
                if (disposed) return;
                disposed = true;
                for (const registration of registrations.reverse()) {
                    registration.dispose();
                }
            },
        };
    }

    public close(): void {
        this.channel.close();
    }

    // ---- client side ----

    private _buildClient(
        iface: InterfaceDefinition<any>,
        opts: GetOptions<TOutCtx>,
    ): Record<string, (params: any) => any> {
        const { serviceId, ...ctxRest } = opts;
        const baseCtx = ctxRest as unknown as TOutCtx;
        const interfaceHash = iface.schemaHash;
        const proxy: Record<string, (p: any) => any> = {};
        for (const [name, member] of Object.entries(iface.members) as [string, MemberType][]) {
            const wireMethod = serviceId ?
                `${serviceId}::${iface.info.id}::${name}` :
                `${iface.info.id}::${name}`;

            if (member.kind === 'request') {
                const hasStream = member.clientStreamSchema !== undefined ||
                    member.serverStreamSchema !== undefined;

                if (hasStream) {
                    // Streaming-enabled call. Returns a Promise<R> augmented
                    // with `send`. `requestId` resolves once the request has
                    // been allocated by the channel; `send` defers until
                    // then so callers can emit client-stream messages
                    // synchronously after the call returns.
                    const serverStreamSchema = member.serverStreamSchema;
                    proxy[name] = (
                        params: unknown,
                        callOpts?: { onMessage?: (m: unknown) => void; },
                    ) => {
                        const userListener = callOpts?.onMessage;
                        // Symmetric with the server-side handler: parse
                        // inbound stream payloads against the declared
                        // schema and drop anything that doesn't match.
                        // The handler always sees validated data.
                        const onStreamMessage = userListener !== undefined ?
                            (raw: JsonValue) => {
                                if (serverStreamSchema !== undefined) {
                                    const r = safeParse(serverStreamSchema, raw);
                                    if (!r.success) return;
                                    userListener(r.data);
                                    return;
                                }
                                userListener(raw);
                            } :
                            undefined;

                        const channelOpts: StreamSendOpts<TOutCtx> = onStreamMessage !== undefined ?
                            { ctx: baseCtx, interfaceHash, onStreamMessage } :
                            { ctx: baseCtx, interfaceHash };
                        const call = this.channel.sendRequestWithStream(
                            wireMethod,
                            params as JsonValue | undefined,
                            channelOpts,
                        );
                        const result = call.result.then((raw) => {
                            const checked = safeParse(
                                (member as RequestType).resultSchema,
                                raw === null ? undefined : raw,
                            );
                            if (!checked.success) {
                                throw new RpcError(
                                    ErrorCode.internalError,
                                    `Invalid result for ${wireMethod}`,
                                    { issues: checked.error.issues as unknown as JsonValue },
                                );
                            }
                            return raw;
                        });

                        return Object.assign(result, {
                            send: (payload: unknown) => call.send(payload as JsonValue),
                            cancel: (reason?: string) => call.cancel(reason),
                            ping: () => call.ping(),
                        });
                    };
                } else {
                    const resultSchema = (member as RequestType).resultSchema;
                    proxy[name] = async (params: unknown) => {
                        const raw = await this.channel.sendRequest(
                            wireMethod,
                            params as JsonValue | undefined,
                            { ctx: baseCtx, interfaceHash },
                        );
                        // Client-side result data validation: the peer might be
                        // a foreign / older server that never validated its own
                        // output, so verify the declared `resultSchema` here too.
                        // Validate as a gate (return `raw` unchanged on success);
                        // a wire `null` maps to `undefined` for the check, matching
                        // the server's `undefined → null` void convention.
                        const checked = safeParse(resultSchema, raw === null ? undefined : raw);
                        if (!checked.success) {
                            throw new RpcError(
                                ErrorCode.internalError,
                                `Invalid result for ${wireMethod}`,
                                { issues: checked.error.issues as unknown as JsonValue },
                            );
                        }
                        return raw;
                    };
                }
            } else {
                proxy[name] = async (params: unknown) => {
                    void this.channel.sendNotification(
                        wireMethod,
                        params as JsonValue | undefined,
                        { ctx: baseCtx, interfaceHash },
                    );
                };
            }
        }
        return proxy;
    }

    // ---- server side ----

    private async _handleRequest(call: IncomingCall<TInCtx>): Promise<Result> {
        const parsed = this._parseRouted(call.method);
        if (!parsed.ok) return notFound(parsed.reason, call.method);

        const { entry, memberName } = parsed;
        const member = entry.iface.members[memberName];
        if (!member) return notFound('unknown-method', call.method);

        const handler = entry.handlers[memberName] as
            | ((params: unknown, ctx: TInCtx, stream: StreamApi<unknown, unknown>) => unknown)
            | undefined;
        if (!handler) return notFound('unknown-method', call.method);

        const parsedParams = safeParse((member as MemberType).paramsSchema, call.params);
        if (!parsedParams.success) {
            return {
                error: {
                    code: ErrorCode.invalidParams,
                    message: 'Invalid params',
                    data: { issues: parsedParams.error.issues as unknown as JsonValue },
                },
            };
        }

        if (!(member instanceof RequestType)) {
            // Caller used request semantics on a notification-only method.
            return notFound('unknown-method', call.method);
        }

        const stream = this._buildStreamApi(call.stream, call.signal, member);
        try {
            const result = await handler(parsedParams.data, call.context, stream);
            // Result data validation: a handler must return what its declared
            // `resultSchema` promises. Symmetric with the inbound `paramsSchema`
            // check above (and the stream-payload checks) — without it, result
            // schemas are compile-time only and a buggy handler ships malformed
            // data that blows up deep in the caller. On a mismatch we fail the
            // call with `internalError` (the fault is the callee's, not the
            // caller's). Conformant results are sent unchanged (we validate as a
            // gate, not to strip), so extra keys behave exactly as before.
            const checked = safeParse((member as MemberType).resultSchema, result);
            if (!checked.success) {
                return {
                    error: {
                        code: ErrorCode.internalError,
                        message: `Invalid result for ${call.method}`,
                        data: { issues: checked.error.issues as unknown as JsonValue },
                    },
                };
            }
            return { result: result === undefined ? null : (result as JsonValue) };
        } catch (e) {
            if (e instanceof RpcError) {
                return { error: { code: e.code, message: e.message, data: e.data } };
            }
            return {
                error: {
                    code: ErrorCode.internalError,
                    message: e instanceof Error ? e.message : String(e),
                },
            };
        }
        // Inbound stream listener is auto-detached by JsonRpcChannel when
        // the handler's response settles — no manual unregister needed.
    }

    private _buildStreamApi(
        callStream: IncomingStream,
        signal: AbortSignal,
        member: RequestType<unknown, unknown, unknown, unknown, unknown>,
    ): StreamApi<unknown, unknown> {
        return {
            send: (payload: unknown) => {
                if (member.serverStreamSchema) {
                    const r = safeParse(member.serverStreamSchema, payload);
                    if (!r.success) {
                        throw new Error(
                            `Server stream payload failed schema validation: ${r.error.message}`,
                        );
                    }
                }
                void callStream.send(payload as JsonValue);
            },
            onMessage: (listener: (payload: unknown) => void) => {
                callStream.onMessage((raw) => {
                    if (member.clientStreamSchema) {
                        const r = safeParse(member.clientStreamSchema, raw);
                        if (!r.success) return; // drop invalid client stream payload
                        listener(r.data);
                        return;
                    }
                    // No declared client stream schema => the method does
                    // not accept client-emitted stream messages. Drop.
                });
            },
            ping: () => callStream.ping(),
            signal,
        };
    }

    private _handleNotification(call: IncomingCall<TInCtx>): void {
        const parsed = this._parseRouted(call.method);
        if (!parsed.ok) return;
        const { entry, memberName } = parsed;
        const member = entry.iface.members[memberName];
        if (!(member instanceof NotificationType)) return;
        const handler = entry.handlers[memberName] as
            | ((params: unknown, ctx: TInCtx) => unknown)
            | undefined;
        if (!handler) return;

        const parsedParams = safeParse(member.paramsSchema, call.params);
        if (!parsedParams.success) return;
        void handler(parsedParams.data, call.context);
    }

    private _parseRouted(method: string):
        | { ok: true; entry: RegisteredInterface; memberName: string; }
        | { ok: false; reason: string; } {
        const parsed = parseMethodName(method);
        if (!parsed) return { ok: false, reason: 'bad-method-grammar' };

        if (parsed.kind === 'bare') {
            if (!this._preset) return { ok: false, reason: 'no-preset' };
            const entry = this._registry.get(`::${this._preset.interfaceId}`);
            if (!entry) return { ok: false, reason: 'no-preset' };
            return { ok: true, entry, memberName: parsed.member };
        }

        const serviceId = parsed.kind === 'full' ? parsed.serviceId : undefined;
        const key = `${serviceId ?? ''}::${parsed.interfaceId}`;
        const entry = this._registry.get(key);
        if (!entry) {
            return {
                ok: false,
                reason: serviceId ? 'unknown-service' : 'unknown-interface',
            };
        }
        return { ok: true, entry, memberName: parsed.member };
    }
}


/**
 * Options when obtaining a typed client for an interface.
 *
 *  - `serviceId`: route to a specific service (form 3). Omit for form 2
 *    (implicit / root service on the connection).
 *  - any `TOutCtx` property: a per-client default merged into the `ctx`
 *    of every call issued through the returned proxy (e.g.
 *    `signerOverride`, `capsOverride` for a signing channel). The
 *    connection forwards these verbatim; it does not interpret them.
 *
 * Schema-version pinning travels as interface-level call metadata
 * ({@link SendOpts.interfaceHash}); the typed proxy stamps
 * `iface.schemaHash` automatically, independent of `TOutCtx`.
 */
export type GetOptions<TOutCtx = undefined> = {
    serviceId?: string;
} & Partial<TOutCtx>;

export interface RegisterOptions {
    /**
     * If set, this interface is mounted under this service id (form 3).
     */
    serviceId?: string;
    /**
     * Optional human description recorded for `serviceId` and surfaced
     * through `hubrpc.directory::list`. Requires `serviceId`. The first
     * registration's description wins; any later registration that
     * supplies a *different* non-undefined description throws.
     */
    serviceDescription?: string;
    /**
     * Root node ids required to access `serviceId`, in CNF (AND of OR-sets):
     * the caller must satisfy **every** set, and a set is satisfied by **any
     * one** of its node ids. Surfaced through `hubrpc.directory::list`.
     * Requires `serviceId`. Recorded per service; a later registration that
     * supplies a *different* value for the same `serviceId` throws.
     */
    rootPrincipalSets?: readonly RootPrincipalSet[];
}

/** A live interface registration. Disposing it removes dispatch and reflection state. */
export interface InterfaceRegistration {
    /** Remove this exact registration. Idempotent. */
    dispose(): void;
}

/** Structural equality for two CNF root-node-id requirement collections. */
function rootPrincipalSetsEqual(
    a: readonly RootPrincipalSet[],
    b: readonly RootPrincipalSet[],
): boolean {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const sa = a[i];
        const sb = b[i];
        if (sa.length !== sb.length) return false;
        for (let j = 0; j < sa.length; j++) {
            if (sa[j].principal !== sb[j].principal) return false;
            if ((sa[j].transitive === true) !== (sb[j].transitive === true)) return false;
        }
    }
    return true;
}

interface RegisteredInterface {
    iface: InterfaceDefinition<any>;
    handlers: Record<string, (params: any, ctx: any) => any>;
    serviceId: string | undefined;
}

interface Preset {
    serviceId: string | undefined;
    interfaceId: string;
    hash: string;
}


/** Service-scoped handle returned by `connection.service(id)`. */
export class ServiceHandle<TInCtx = undefined, TOutCtx = undefined> {
    constructor(
        private readonly _connection: HubRpcConnection<TInCtx, TOutCtx>,
        private readonly _serviceId: string,
    ) { }

    public get<TDef extends InterfaceDefinition<any>>(
        iface: TDef,
        opts: Partial<TOutCtx> = {},
    ): InterfaceClient<TDef> {
        return this._connection.get(iface, { ...opts, serviceId: this._serviceId } as GetOptions<TOutCtx>);
    }

    public register<TDef extends InterfaceDefinition<any>>(
        iface: TDef,
        handlers: InterfaceHandlers<TDef, TInCtx>,
        opts: Omit<RegisterOptions, 'serviceId'> = {},
    ): InterfaceRegistration {
        return this._connection.register(iface, handlers, { ...opts, serviceId: this._serviceId });
    }
}

function notFound(reason: string, method: string): Result {
    return {
        error: {
            code: ErrorCode.methodNotFound,
            message: `Method not found: ${method}`,
            data: { reason, method },
        },
    };
}
